import { type ReactNode } from 'react'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import { ActionToggle } from '@/components/ActionToggle'
import { FormGroupContainer } from '@/components/FormContainer'
import { FormRow } from '@/components/Form'
import styles from './ManageExtensionBillingInfo.module.css'
import cx from 'classnames'
import { IconButton } from '@/components/IconButton'
import type {
  InventoryItemExtensionBillingType,
  InventoryItemExtensionType,
} from '@/features/inventory/InventoryItem'

export interface ManageExtensionBillingInfoProps {
  extensionType: InventoryItemExtensionType
  onExtensionTypeChange: (extensionType: InventoryItemExtensionType) => void
  billingType: InventoryItemExtensionBillingType
  onBillingTypeChange: (billingType: InventoryItemExtensionBillingType) => void
  isJobProgressionBillingEnabled: boolean
  onJobProgressionBillingEnabledChange: (enabled: boolean) => void
  editing?: boolean
  overridden?: boolean
  /**
   * The billing editor matching `isJobProgressionBillingEnabled`.
   */
  children?: ReactNode
  testId?: string
}

export function ManageExtensionBillingInfo({
  extensionType,
  onExtensionTypeChange,
  billingType,
  onBillingTypeChange,
  isJobProgressionBillingEnabled,
  onJobProgressionBillingEnabledChange,
  editing,
  overridden,
  children,
  testId,
}: ManageExtensionBillingInfoProps) {
  return (
    <>
      <FormRow>
        <FormGroupContainer label="Extension type">
          <RadioButtonGroup size="big-wide">
            <RadioButtonItem
              onPress={() => onExtensionTypeChange('Personal')}
              active={extensionType === 'Personal'}
              isDisabled={editing || overridden}
              testId={
                testId ? `${testId}-extension-type-personal-button` : undefined
              }
            >
              Personal
            </RadioButtonItem>
            <RadioButtonItem
              onPress={() => onExtensionTypeChange('Business')}
              active={extensionType === 'Business'}
              isDisabled={editing || overridden}
              testId={
                testId ? `${testId}-extension-type-business-button` : undefined
              }
            >
              Business
            </RadioButtonItem>
            <RadioButtonItem
              onPress={() => onExtensionTypeChange('Outsourcing')}
              active={extensionType === 'Outsourcing'}
              isDisabled={editing || overridden}
              testId={
                testId
                  ? `${testId}-extension-type-outsourcing-button`
                  : undefined
              }
            >
              Outsourcing
            </RadioButtonItem>
          </RadioButtonGroup>
        </FormGroupContainer>
      </FormRow>

      <FormRow>
        <FormGroupContainer label="Billing type">
          <RadioButtonGroup size="big-wide">
            <RadioButtonItem
              onPress={() => onBillingTypeChange('FlatFee')}
              active={billingType === 'FlatFee'}
              isDisabled={editing || overridden}
              testId={
                testId
                  ? `${testId}-extension-billing-type-flat-fee-button`
                  : undefined
              }
            >
              Flat fee
            </RadioButtonItem>
            <RadioButtonItem
              onPress={() => onBillingTypeChange('Percentage')}
              active={billingType === 'Percentage'}
              isDisabled={editing || overridden}
              testId={
                testId
                  ? `${testId}-extension-billing-type-percentage-button`
                  : undefined
              }
            >
              Percentage
            </RadioButtonItem>
          </RadioButtonGroup>
        </FormGroupContainer>
      </FormRow>
      <FormRow>
        <ActionToggle
          label="Job Progression Dependant"
          description=""
          value={isJobProgressionBillingEnabled}
          onChange={() =>
            onJobProgressionBillingEnabledChange(
              !isJobProgressionBillingEnabled,
            )
          }
          testId={
            testId
              ? `${testId}-modal-extension-job-progression-billing-info`
              : undefined
          }
        />
      </FormRow>
      {children}
    </>
  )
}

export interface JobProgressionBillingProgression {
  id: string
  name: string
  stepNames: string[]
  onEdit: () => void
  onRemove: () => void
}

export interface JobProgressionBillingProps {
  selectedCount: number
  canAddProgression: boolean
  onAddProgression: () => void
  progressions: JobProgressionBillingProgression[]
}

export function JobProgressionBilling({
  selectedCount,
  canAddProgression,
  onAddProgression,
  progressions,
}: JobProgressionBillingProps) {
  return (
    <FormRow>
      <FormGroupContainer>
        <label className={styles.supportingItemsLabel}>
          <div>
            Job Progression
            <span className={styles.supportingItemCount}>
              ({selectedCount})
            </span>
          </div>

          {canAddProgression && (
            <IconButton
              name="add"
              label="Add job progression"
              className={styles.extensionJobProgressionRowManage}
              onPress={() => onAddProgression()}
              size={24}
            />
          )}
        </label>
        {selectedCount === 0 && (
          <div
            className={cx(styles.extensionJobProgression, styles.noneAssigned)}
          >
            None assigned
          </div>
        )}

        {progressions.map((progression) => (
          <div
            key={progression.id}
            className={styles.extensionJobProgressionContainer}
          >
            <div className={styles.extensionJobProgressionText}>
              <div className={styles.extensionJobProgressionTitle}>
                {progression.name}
              </div>

              <div className={styles.extensionJobProgressionSteps}>
                {progression.stepNames.map(
                  (stepName, index) =>
                    `${stepName} ${index === progression.stepNames.length - 1 ? '' : '| '}`,
                )}
              </div>
            </div>

            <div className={styles.extensionJobProgressionActions}>
              <IconButton
                name="edit-outlined"
                label="Edit job progression"
                onPress={progression.onEdit}
                size={24}
              />

              <IconButton
                name="close"
                label="Remove job progression"
                onPress={progression.onRemove}
                size={24}
              />
            </div>
          </div>
        ))}
      </FormGroupContainer>
    </FormRow>
  )
}
